import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation, FilterType } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";
import HtmlWrapper from "commons/ui/HtmlWrapper";

import { makeUrl } from "commons/utils/http";

import useProjectApi from "hooks/api/private/useProjectApi";
import useSnackbar from "hooks/useSnackbar";

import { ProjectListItem } from "types/api/project";

import { firstLetterUpperCase } from "utils/utils";

export type ProjectsListHook = {
    navigateToNewProject: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    dataTableHook: DataTableHook<ProjectListItem>;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useProjectsList(): ProjectsListHook {
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    title: {
                        key: "title",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    description: {
                        key: "description",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    slug: {
                        key: "slug",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    place: {
                        key: "place",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "projects-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.title = value;
                newForm.search.description = value;
                newForm.search.slug = value;
                newForm.search.place = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            enabled: {
                key: "enabled",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            category: {
                key: "category",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "projects-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { getAllProject, deleteProject } = useProjectApi();

    const navigate = useNavigate();

    const navigateToEditProject = React.useCallback(
        (row: ProjectListItem) => {
            navigate(makeUrl(`${row.id}/modifica`));
        },
        [navigate],
    );

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina progetto",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteProjectButton = React.useCallback(
        (row: ProjectListItem) => {
            openConfirmAlert("Sei sicuro di voler eliminare il Progetto?", () => {
                const [apiCall] = deleteProject({ id: row.id });

                apiCall
                    .then(() => {
                        setTriggerReloadTable(true);
                        enqueueSuccessSnackbar("Progetto eliminato con successo");
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del progetto"));
            });
        },
        [
            enqueueSuccessSnackbar,
            setTriggerReloadTable,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteProject,
        ],
    );

    const columns: Array<Column<ProjectListItem>> = React.useMemo(
        () => [
            {
                id: "title",
                header: "Titolo",
                render: (row: ProjectListItem) => <HtmlWrapper htmlString={row.title} />,
            },
            {
                id: "place",
                header: "Luogo",
                render: (row: ProjectListItem) => <HtmlWrapper htmlString={row.place} />,
            },
            {
                id: "category",
                header: "Categoria",
                render: (row: ProjectListItem) => firstLetterUpperCase(row.category),
            },
            {
                id: "enabled",
                header: "Area pubblica",
                width: "130px",
                render: (row: ProjectListItem) => (row.enabled ? "Visibile" : "Non visibile"),
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: ProjectListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditProject}
                        onDelete={deleteProjectButton}
                    />
                ),
            },
        ],
        [navigateToEditProject, deleteProjectButton],
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const dataTableHook = useDataTableWithMemory<ProjectListItem>({
        apiCall: getAllProject,
        getElements: calculateRows,
        columns: columns,
        filters: filters,
        size: 20,
        key: "projects-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewProject = React.useCallback(() => {
        navigate("/progetti/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): ProjectsListHook => ({
            onSearchFilterChange,
            navigateToNewProject,
            filterSearchHook,
            staticFilterHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewProject,
            filterSearchHook,
            alertConfirmHook,
            staticFilterHook,
            dataTableHook,
        ],
    );
}

export default useProjectsList;
